import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Polyfill for requestIdleCallback
export const requestIdleCallbackPolyfill = (callback) => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    return window.requestIdleCallback(callback);
  }
  // Fallback to setTimeout with a reasonable timeout
  return setTimeout(() => callback(), 50);
};

/**
 * Generic hook for handling list-detail navigation with grid layouts
 * 
 * @param {Object} options Configuration options
 * @param {Object} options.gridRef Reference to the grid component
 * @param {Array} options.items Array of items displayed in the grid
 * @param {Number} options.columnCount Number of columns in the grid
 * @param {Number} options.rowHeight Height of each row in pixels (default: 230)
 * @param {Object} options.routes Route configuration
 * @param {String} options.routes.list Base path for the list view
 * @param {Function} options.routes.detail Function that returns the detail path for an item (id) => `/path/${id}`
 * @param {String} options.routes.new Path for creating a new item
 * @param {Object} options.config Configuration for item fields
 * @param {String} options.config.idField Field name for the item ID (default: 'id')
 * @param {Function} options.onReturnFromDetail Optional callback when returning from detail view
 * @param {Function} options.loadMoreDataCallback Function to call to load more data when scrolling
 * @param {Object} options.pageInfo Pagination information containing hasNextPage
 * @param {Boolean} options.isLoadingMore Flag indicating if more data is being loaded
 * @param {Function} options.onNavigateAway Function called when user navigates away from entire section
 * @returns {Object} Navigation state and handlers
 */
const useGridNavigation = ({
  gridRef,
  items,
  columnCount,
  rowHeight = 230,
  routes = {
    list: '/',
    detail: (id) => `/${id}`,
    new: '/new',
    additionalRoutes: []
  },
  config = {
    idField: 'id'
  },
  onReturnFromDetail = () => {},
  loadMoreDataCallback = null,
  pageInfo = { hasNextPage: false },
  isLoadingMore = false,
  onNavigateAway = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const paramId = params.id;
  
  // Check if we're on an additional route
  const isAdditionalRoute = routes.additionalRoutes.some(route => location.pathname.includes(route.path));

  // Check if the current additional route should not load data
  const additionalRouteShouldNotLoadData = routes.additionalRoutes.find(
    route => location.pathname.includes(route.path) && route.shouldLoadData === false
  );
  
  // Check if we're on the new item route
  const isNewItem = location.pathname.includes(routes.new);
  const hasItems = items.length > 0;
  
  // UI state for navigation
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const [isReturningFromDetail, setIsReturningFromDetail] = useState(false);
  const [shouldHideContent, setShouldHideContent] = useState(false);
  
  // References for tracking state
  const initialScrollRestored = useRef(false);
  const prevLocationRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const dataProcessedRef = useRef(false);
  const targetScrollPositionRef = useRef(null);
  const isRestoringNavigationScrollRef = useRef(false); // Tracks if we're currently restoring scroll
  const hasInitiallyRestoredRef = useRef(false); // Tracks if we've already done the initial restoration
  const userHasScrolledRef = useRef(false); // Tracks if user has manually scrolled
  
  // Helper function to determine if data should be loaded
  // This centralizes the common checks used in component effects
  const shouldLoadData = useCallback(() => {
    // Don't load data if we're in a detail view
    if (paramId) {
      return false;
    }

    // Don't load data if we're in a new item view
    if (isNewItem) {
      return false;
    }

    // Don't load data if we're on an additional route that shouldn't load data
    if (additionalRouteShouldNotLoadData) {
      return false;
    }

    // Don't load data if we're not on the list route
    if (location.pathname !== routes.list) {
      return false;
    }
    
    // Special case: if items array is empty, we should always load data
    // This handles cases where we return from a detail page after a refresh
    if (items.length === 0) {
      return true;
    }
    
    // Don't load data if we're returning from a detail view
    // This prevents unnecessary data loading when we already have the data
    if (location.state?.fromDetail) {
      return false;
    }
    
    // Don't load data if we have the needsRestore flag (coming back from detail)
    if (location.state?.needsRestore) {
      return false;
    }
    
    // Don't load data if we're actively loading data (initial load or pagination)
    // This is important to prevent reloading during pagination
    if (isLoadingMore) {
      return false;
    }
    
    // All checks passed, we should load data
    return true;
  }, [paramId, isNewItem, additionalRouteShouldNotLoadData, location.pathname, routes.list, location.state?.fromDetail, location.state?.needsRestore, items.length, isLoadingMore]);
  
  // Helper function to check if we should force a reload
  // This is separate from shouldLoadData to handle specific edge cases
  const shouldForceReload = useCallback(() => {
    // Always force reload if items array is empty
    const result = items.length === 0;
    return result;
  }, [items.length]);
  
  // Helper function to check if we're returning from a detail page
  const isReturningFromDetailPage = useCallback(() => {
    const result = Boolean(location.state?.fromDetail || location.state?.needsRestore);
    return result;
  }, [location.state]);
  
  // High-performance scroll handler with true debounce
  const handleGridScroll = useCallback(({ scrollTop, scrollUpdateWasRequested }) => {
    // Only handle user-initiated scrolls
    if (scrollUpdateWasRequested) return;
    
    // Update the ref immediately for smooth tracking
    scrollPositionRef.current = scrollTop;
    
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  // Function to mark that the user has scrolled manually
  // This is important to prevent jumping back during pagination
  const markUserHasScrolled = useCallback(() => {
    userHasScrolledRef.current = true;
    
    // If we've already restored once, we can clear the target position
    if (hasInitiallyRestoredRef.current) {
      targetScrollPositionRef.current = null;
    }
  }, []);

  // Handle item click with navigation and scroll state saving
  const handleItemClick = useCallback((item) => {
    // Save the current scroll position before navigating
    if (gridRef.current && gridRef.current._outerRef) {
      const currentScrollPosition = gridRef.current._outerRef.scrollTop;
      
      // Get additional data about the item
      const itemIndex = items.findIndex(i => i[config.idField] === item[config.idField]);
      let rowIndex = -1;
      let itemTopPosition = 0;
      let offsetFromTop = 0;
      
      if (itemIndex !== -1) {
        rowIndex = Math.floor(itemIndex / columnCount);
        itemTopPosition = rowIndex * rowHeight; 
        offsetFromTop = itemTopPosition - currentScrollPosition;
      }
      
      // Create a state object with all the scroll data
      const scrollState = {
        scrollPosition: currentScrollPosition,
        itemId: item[config.idField],
        itemIndex,
        rowIndex,
        offsetFromTop,
        timestamp: Date.now()
      };
      
      
      // Reset restoration flags and hide spinner before navigating to details
      setIsRestoringScroll(false);
      setShowLoadingSpinner(false);
      setIsReturningFromDetail(false);
      setShouldHideContent(false);
      
      // Reset tracking flags when navigating to a detail
      hasInitiallyRestoredRef.current = false;
      userHasScrolledRef.current = false;
      
      navigate(routes.detail(item[config.idField]), { state: { fromList: true, scrollState } });
    } else {
      // Navigate without state if no grid ref
      navigate(routes.detail(item[config.idField]));
    }
  }, [gridRef, items, columnCount, navigate, routes, config.idField, rowHeight]);

  // Navigate to a custom path while preserving scroll state
  // This is useful for view-only routes or other custom navigation paths
  const navigateWithScrollState = useCallback((item, customPath) => {
    if (gridRef.current && gridRef.current._outerRef) {
      const currentScrollPosition = gridRef.current._outerRef.scrollTop;

      const itemIndex = items.findIndex(i => i[config.idField] === item[config.idField]);
      let rowIndex = -1;
      let itemTopPosition = 0;
      let offsetFromTop = 0;

      if (itemIndex !== -1) {
        rowIndex = Math.floor(itemIndex / columnCount);
        itemTopPosition = rowIndex * rowHeight;
        offsetFromTop = itemTopPosition - currentScrollPosition;
      }

      const scrollState = {
        scrollPosition: currentScrollPosition,
        itemId: item[config.idField],
        itemIndex,
        rowIndex,
        offsetFromTop,
        timestamp: Date.now()
      };

      // Reset restoration flags before navigating
      hasInitiallyRestoredRef.current = false;
      userHasScrolledRef.current = false;

      navigate(customPath, { state: { fromList: true, scrollState } });
    } else {
      navigate(customPath);
    }
  }, [gridRef, items, columnCount, rowHeight, navigate, config.idField]);

  // Handle back button click
  const handleBackToItems = useCallback(() => {
    // Get the scroll state from the location state - check multiple possible locations
    const scrollState = location.state?.scrollState || 
                       location.state?.originalScrollState ||
                       location.state?.fromList?.scrollState;
    
    
    // Hide content immediately to prevent showing unscrolled content
    setShouldHideContent(true);
    
    // Store the target scroll position for immediate application on mount
    if (scrollState?.scrollPosition !== undefined) {
      targetScrollPositionRef.current = scrollState.scrollPosition;
    }
    
    // Set restoration flag for scroll position
    setIsRestoringScroll(true);
    setIsReturningFromDetail(true);
    
    // Signal to caller that we're returning from details
    // This is crucial for data loading coordination
    onReturnFromDetail(true);
    dataProcessedRef.current = false;
    
    // Reset tracking flags when returning to list
    hasInitiallyRestoredRef.current = false;
    userHasScrolledRef.current = false;
    
    // Use setTimeout to ensure React state update happens before navigation
    setTimeout(() => {
      // Navigate back with the scroll state but without forcing a refresh
      navigate(routes.list, { 
        state: { 
          fromDetail: true,
          scrollState,
          needsRestore: true, // Explicit flag for restoration
          hideContentUntilScrolled: true, // New flag to prevent content flash
          timestamp: Date.now() // Add timestamp to ensure state is considered "new" by React Router
        }
      });
    }, 10);
  }, [location.state, navigate, routes.list, onReturnFromDetail]);

  // Handle Add New Item button click
  const handleAddNew = useCallback(() => {
    // Add a flag to prevent position restoration when returning from new item
    navigate(routes.new, { 
      state: { fromList: true, createNew: true }
    });
    
    // Reset restoration states and hide spinner
    setIsRestoringScroll(false);
    setShowLoadingSpinner(false);
    setIsReturningFromDetail(false);
    setShouldHideContent(false);
  }, [navigate, routes.new]);

  // Custom back handler for returning from new item form
  const handleCustomBack = useCallback(() => {
    // Mark this as coming from a new item to prevent restoration loops
    // and force data refresh when returning to the list
    navigate(routes.list, {
      state: { 
        fromNew: true,  // Flag to prevent restoration
        clearRestoration: true, // Signal to clear any restoration state
        // Only force refresh for new items, not for edits that already update the store
        forceRefresh: isNewItem 
      }
    });
    
    // Make sure content is visible for this navigation
    setShouldHideContent(false);
  }, [isNewItem, navigate, routes.list]);
  
  // Track navigation and scroll restoration state
  useEffect(() => {
    // Get scroll state from location state (passed during navigation)
    const fromDetail = location.state?.fromDetail;
    const scrollState = location.state?.scrollState;
    const hideContent = location.state?.hideContentUntilScrolled;
    
    // Early exit if not returning from detail
    if (!fromDetail || !scrollState) {
      return;
    }
    
    // Important: only set shouldHideContent once, when we first detect the return from detail
    if (fromDetail && hideContent && !dataProcessedRef.current) {
      setShouldHideContent(true);
    }
    
    // Only process once to prevent loops
    if (!dataProcessedRef.current) {
      // Show loading spinner and update returning state
      setShowLoadingSpinner(true);
      setIsReturningFromDetail(true);
      
      // Signal to caller that we're returning from detail view
      onReturnFromDetail(true);
      
      // Store the current location pathname
      prevLocationRef.current = location.pathname;
      
      // Save target scroll position for immediate use
      if (scrollState?.scrollPosition !== undefined) {
        targetScrollPositionRef.current = scrollState.scrollPosition;
      }
      
      // Mark that we've processed this navigation to prevent duplicate effects
      dataProcessedRef.current = true;
      
      // Hide spinner after a reasonable delay
      const spinnerTimeout = setTimeout(() => {
        setShowLoadingSpinner(false);
      }, 500);
      
      return () => clearTimeout(spinnerTimeout);
    }
  }, [location.state, onReturnFromDetail]);

  // Apply the initial scroll position immediately upon mounting/data loading
  useEffect(() => {
    // Only run if we're returning from detail and have a target position
    // and we haven't already restored the position
    if (
      isReturningFromDetail && 
      targetScrollPositionRef.current !== null && 
      gridRef.current && 
      gridRef.current._outerRef &&
      items.length > 0 && 
      shouldHideContent &&
      !hasInitiallyRestoredRef.current
    ) {
      
      // Set the flag to indicate navigation-based scroll restoration is in progress
      isRestoringNavigationScrollRef.current = true;
      
      // Delay the scroll restoration slightly to ensure the DOM has fully rendered
      // This is critical for proper scroll position restoration
      setTimeout(() => {
        const gridElement = gridRef.current?._outerRef;
        if (!gridElement) return;
        
        const targetPosition = targetScrollPositionRef.current;
        
        // Calculate max scroll to avoid issues
        const gridScrollHeight = gridElement.scrollHeight;
        const gridClientHeight = gridElement.clientHeight;
        const maxScroll = Math.max(0, gridScrollHeight - gridClientHeight);
        
        // Apply scroll position
        const safeScrollPosition = Math.min(targetPosition, maxScroll);
        gridElement.scrollTop = safeScrollPosition;
        
        // Mark that we've performed the initial restoration
        hasInitiallyRestoredRef.current = true;
        
        // Make multiple attempts to ensure scroll position is set correctly
        const confirmScrollTimer = setTimeout(() => {
          if (gridElement.scrollTop !== safeScrollPosition) {
            gridElement.scrollTop = safeScrollPosition;
          }
        }, 50);
        
        // Show the content after ensuring scroll is set
        const showContentTimer = setTimeout(() => {
          setShouldHideContent(false);
        }, 100);
        
        return () => {
          clearTimeout(confirmScrollTimer);
          clearTimeout(showContentTimer);
        };
      }, 50);
      
      // For extra safety, clear navigation flag after a reasonable time
      const clearFlagsTimer = setTimeout(() => {
        // Once initial restoration is done, we don't want to restore from navigation anymore
        isRestoringNavigationScrollRef.current = false;
      }, 300);
      
      // Safety fallback - if for any reason the content is still hidden after a delay, force show it
      const forcedResetTimer = setTimeout(() => {
        setShouldHideContent(false);
        setIsReturningFromDetail(false);
        setShowLoadingSpinner(false);
        
        // Clear the flag when forced reset happens
        isRestoringNavigationScrollRef.current = false;
      }, 1000);
      
      return () => {
        clearTimeout(clearFlagsTimer);
        clearTimeout(forcedResetTimer);
        // Ensure flag is cleared if component unmounts
        isRestoringNavigationScrollRef.current = false;
      };
    } else if (isReturningFromDetail && items.length > 0) {
      // If we have items but couldn't restore scroll for some reason, at least show the content
      const forcedShowTimer = setTimeout(() => {
        setShouldHideContent(false);
        setShowLoadingSpinner(false);
        
        // Clear the flag when we give up on scroll restoration
        isRestoringNavigationScrollRef.current = false;
      }, 100);
      
      return () => clearTimeout(forcedShowTimer);
    }
  }, [items.length, gridRef.current, isReturningFromDetail, shouldHideContent]);

  // Add a scroll listener to detect user scrolling
  useEffect(() => {
    // Don't set up the listener if we're on a detail page or new item page
    if (paramId || isNewItem) {
      return;
    }
    
    // Only add listener when we have items and grid ref
    if (!gridRef.current || !gridRef.current._outerRef || items.length === 0) {
      return;
    }
    
    // Once the user starts scrolling, mark that they've scrolled
    const handleUserScroll = () => {
      // Only consider it user scrolling if it's not from a programmatic change
      if (!isRestoringNavigationScrollRef.current) {
        markUserHasScrolled();
      }
    };
    
    // Add scroll listener to detect manual user scrolling
    gridRef.current._outerRef.addEventListener('scroll', handleUserScroll, { passive: true });
    
    return () => {
      if (gridRef.current && gridRef.current._outerRef) {
        gridRef.current._outerRef.removeEventListener('scroll', handleUserScroll);
      }
    };
  }, [paramId, isNewItem, items.length, gridRef.current, markUserHasScrolled]);

  // Handle loading more data with scroll position preservation
  const loadMoreData = useCallback(() => {
    // If no callback is provided, do nothing
    if (!loadMoreDataCallback) {
      return;
    }

    // NOTE: Removed special case for items.length === 0 to prevent infinite loops
    // The initial data fetch should be handled by the component's useEffect, not here
    
    // Check if we actually have more pages to load
    if (!pageInfo.hasNextPage) {
      return;
    }
    
    // Check if already loading more data (moved from component callbacks)
    if (isLoadingMore) {
      return;
    }
    
    // If user has scrolled manually, clear the target position
    // This prevents jumping when loading more data after user scrolling
    if (userHasScrolledRef.current) {
      targetScrollPositionRef.current = null;
    }
    
    // Get the scroll container directly from the grid ref for more reliability
    const gridElement = gridRef.current?._outerRef;
    if (!gridElement) {
      return;
    }
    
    // Store current scroll position before loading more data
    const scrollPosition = gridElement.scrollTop;
    
    
    // Store the current count before loading
    const currentItemCount = items.length;
    
    // Call the provided callback to load more data
    const loadPromise = loadMoreDataCallback();
    
    // If the callback returns a promise, process it for scroll preservation
    if (loadPromise && typeof loadPromise.then === 'function') {
      loadPromise.then(() => {
        // Check if new items were loaded
        const newItemCount = items.length;
        const newItemsLoaded = newItemCount - currentItemCount;
        
        
        // Only preserve scroll if we loaded new items
        if (newItemsLoaded > 0) {
          // We need to restore scroll in multiple ways to ensure it works:
          
          // 1. Immediate restoration attempt
          if (gridElement.scrollTop !== scrollPosition) {
            gridElement.scrollTop = scrollPosition;
          }
          
          // 2. Delayed restoration to ensure the DOM has updated
          setTimeout(() => {
            if (gridElement && gridElement.scrollTop !== scrollPosition) {
              gridElement.scrollTop = scrollPosition;
            }
          }, 10);
          
          // 3. Use requestAnimationFrame for smoother visual restoration
          requestAnimationFrame(() => {
            if (gridElement && gridElement.scrollTop !== scrollPosition) {
              gridElement.scrollTop = scrollPosition;
            }
          });
          
          // 4. Use requestIdleCallback as a final fallback
          requestIdleCallbackPolyfill(() => {
            if (gridElement && gridElement.scrollTop !== scrollPosition) {
              gridElement.scrollTop = scrollPosition;
            }
          });
        }
      }).catch(err => {
        console.error('Error loading more data:', err);
      });
    }
  }, [loadMoreDataCallback, items.length, pageInfo.hasNextPage, isLoadingMore, gridRef]);

  // Add a combined scroll handler for both tracking and pagination
  const handleCombinedGridScroll = useCallback((scrollInfo) => {
    // Mark that the user has scrolled if this is not a programmatic update
    if (!scrollInfo.scrollUpdateWasRequested) {
      markUserHasScrolled();
    }
    
    // First call the basic scroll handler
    handleGridScroll(scrollInfo);
    
    // Don't trigger pagination for programmatic scrolls
    if (scrollInfo.scrollUpdateWasRequested || !loadMoreDataCallback) {
      return;
    }
    
    // Check if we're near the bottom to trigger pagination
    const gridElement = gridRef.current?._outerRef;
    if (!gridElement) return;
    
    const { scrollTop, scrollHeight, clientHeight } = gridElement;
    const scrollThreshold = 50; // Load more when within 50px of bottom
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    
    // Only load more if we actually have more pages AND we're near the bottom
    if (distanceToBottom < scrollThreshold && pageInfo.hasNextPage) {
      // Use debounce to avoid too many calls while scrolling
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        loadMoreData();
        scrollTimeoutRef.current = null;
      }, 100);
    }
  }, [handleGridScroll, loadMoreData, gridRef, pageInfo.hasNextPage, markUserHasScrolled]);

  // Add a function to force clear all navigation state
  const forceClearHiddenContent = useCallback(() => {
    setShowLoadingSpinner(false);
    setIsRestoringScroll(false);
    setIsReturningFromDetail(false);
    setShouldHideContent(false);
    dataProcessedRef.current = true; // Set to true to prevent reprocessing
    isRestoringNavigationScrollRef.current = false;
    userHasScrolledRef.current = true; // Assume user has scrolled to prevent jumps
  }, []);

  // Handle navigation away from section (added cleanup effect)
  useEffect(() => {
    if (!onNavigateAway || !routes.list) {
      return; // Skip if no cleanup callback or list route provided
    }

    // Extract the section base path from routes.list
    // This handles both absolute paths ('/section') and relative paths ('section')
    const sectionPath = routes.list.startsWith('/')
      ? routes.list.split('/')[1] // For '/section/list' -> 'section'
      : routes.list.split('/')[0]; // For 'section/list' -> 'section'

    return () => {
      // Only run cleanup when navigating completely away from the section
      const currentPath = window.location.pathname;
      if (sectionPath && !currentPath.includes(`/${sectionPath}`)) {
        // Call the cleanup function when navigating away from section
        onNavigateAway();
      }
    };
  }, [onNavigateAway, routes.list]);

  return {
    // Navigation state
    showLoadingSpinner,
    isRestoringScroll,
    isReturningFromDetail,
    shouldHideContent,
    isNewItem,
    hasItems,
    // Navigation actions
    handleGridScroll,
    handleCombinedGridScroll,
    handleItemClick,
    handleBackToItems,
    handleAddNewItem: handleAddNew,
    handleCustomBack,
    navigateWithScrollState,
    
    // Pagination
    loadMoreData,
    
    // Data loading helpers
    shouldLoadData,
    shouldForceReload,
    isReturningFromDetailPage,
    
    // Utility for emergency clearing of state
    forceClearHiddenContent,
  };
};

export default useGridNavigation; 